import type { ApiNotification } from "@/types/entities/notifications";
import type {
  Notification,
  NotificationDetail,
  GroupedNotifications,
} from "./types";

/**
 * Groups notifications by time_group from API.
 */
export const groupNotifications = (
  notifications: Notification[],
): GroupedNotifications => {
  const now: Notification[] = [];
  const yesterday: Notification[] = [];
  const past: Notification[] = [];

  notifications.forEach((notification) => {
    const timeGroup = (notification as Notification & { timeGroup?: string })
      .timeGroup;
    switch (timeGroup) {
      case "now":
        now.push(notification);
        break;
      case "yesterday":
        yesterday.push(notification);
        break;
      case "past":
      default:
        past.push(notification);
        break;
    }
  });

  return { now, yesterday, past };
};

/**
 * Maps API notification type to component detail type
 */
const mapNotificationTypeToDetail = (
  apiType: string,
  metadata: ApiNotification["metadata"],
): NotificationDetail | undefined => {
  const snapshot = metadata?.snapshot;

  // Check for signature error in general type notifications
  if (apiType === "general" && metadata?.signed_document_status === "failed") {
    return {
      type: "signature-error",
      errorMessage: "Error de firma",
      metadata,
    };
  }

  switch (apiType) {
    case "assignment":
      return {
        type: "status-assignment",
        assignedStatus:
          (snapshot?.after?.assignee as string) || "Usuario asignado",
      };
    case "reassignment":
      return {
        type: "status-change",
        fromStatus: (snapshot?.before?.assignee as string) || "Sin asignar",
        toStatus: (snapshot?.after?.assignee as string) || "Usuario",
      };
    case "status_change":
      return {
        type: "status-change",
        fromStatus: (snapshot?.before?.status as string) || "Estado anterior",
        toStatus: (snapshot?.after?.status as string) || "Estado nuevo",
      };
    case "comment":
      return {
        type: "comment",
        comment: metadata?.comment || "",
      };
    case "approval":
      return {
        type: "status-assignment",
        assignedStatus: "Aprobado",
      };
    case "rejection":
      return {
        type: "status-assignment",
        assignedStatus: "Rechazado",
      };
    case "signature_error":
      return {
        type: "signature-error",
        documentName: metadata?.document_name || "Documento.pdf",
        errorMessage: (metadata?.error_message as string) || "Error de firma",
      };
    case "revision_requested":
      return {
        type: "status-assignment",
        correction_fields_approved:
          metadata?.correction_fields_approved as number,
        correction_fields_denied: metadata?.correction_fields_denied as number,
      };
    case "correction_rejection":
      return {
        type: "correction_rejection",
      };
    case "extension_request":
      return {
        type: "extension_request",
        metadata,
      };
    case "suspension_request":
      return {
        type: "suspension_request",
        metadata,
      };
    case "appeal_submitted":
      return {
        type: "appeal_submitted",
        metadata,
      };
    case "appeal_rejected":
      return {
        type: "appeal_rejected",
        metadata,
      };
    case "appeal_accepted":
      return {
        type: "appeal_accepted",
        metadata,
      };
    case "document_generator_failed": {
      return {
        type: "document_generator_failed",
        documentName: metadata?.document_name || "Documento.pdf",
        errorMessage: "fallida",
      };
    }
    default:
      return undefined;
  }
};

/**
 * Formats the date from API to display format
 */
const formatNotificationDate = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Hace un momento";
  if (diffMins < 60) return `Hace ${diffMins} minutos`;
  if (diffHours < 24) return `Hace ${diffHours} horas`;
  if (diffDays === 1) return "Ayer";

  // Format as "Oct 10, 2025 | 13:28"
  const options: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
    year: "numeric",
  };
  const formattedDate = date.toLocaleDateString("es-ES", options);
  const formattedTime = date.toLocaleTimeString("es-ES", {
    hour: "2-digit",
    minute: "2-digit",
  });

  return `${formattedDate} | ${formattedTime}`;
};

/**
 * Transforms API notification to component notification format
 */
export const transformApiNotification = (
  apiNotification: ApiNotification,
): Notification & { timeGroup: string } => {
  const { causer, metadata, link } = apiNotification;

  // Build form link if available
  const formLink =
    metadata?.form_name && link
      ? {
          label: metadata.form_name,
          url: link,
        }
      : undefined;

  // Build request link if available
  const requestLink =
    metadata?.formality_code && link
      ? {
          label: `${metadata.formality_code}`,
          url: link,
        }
      : undefined;

  // Handle optional causer - use title or "Sistema" as fallback
  const username =
    causer?.full_name || causer?.name || apiNotification.title || "Sistema";
  const initial = (causer?.name || apiNotification.title || "S")
    .charAt(0)
    .toUpperCase();

  // Append insolvency_reason in parentheses when present
  const insolvencyReason = metadata?.insolvency_reason as string | undefined;
  const description = insolvencyReason
    ? `${apiNotification.message.trimEnd().replace(/\.$/, "")} (${insolvencyReason}).`
    : apiNotification.message;

  return {
    id: apiNotification.id,
    username,
    initial,
    date: formatNotificationDate(apiNotification.created_at),
    description,
    formLink,
    requestLink,
    isRead: apiNotification.is_read,
    detail: mapNotificationTypeToDetail(apiNotification.type, metadata),
    category: apiNotification.type === "general" ? "general" : "personal",
    longContent:
      apiNotification.type === "general" ? apiNotification.message : undefined,
    timeGroup: apiNotification.time_group,
    metadata: apiNotification.metadata,
  };
};

/**
 * Transforms array of API notifications to component format
 */
export const transformApiNotifications = (
  apiNotifications: ApiNotification[],
): (Notification & { timeGroup: string })[] => {
  return apiNotifications.map(transformApiNotification);
};
